import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPollByPost, unvotePoll, votePoll } from "../../Services/engagementService";
import "./PollCard.css";

/**
 * Renders poll attached to a post (loads by PostId). Hidden when no poll exists.
 * @param {boolean} eager - fetch immediately (modal / detail page). Default lazy via viewport.
 * @param {boolean} nested - compact layout inside a shared/quoted original post.
 */
const PollCard = ({ postId, eager = false, nested = false }) => {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const [poll, setPoll] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(eager);

  useEffect(() => {
    if (eager) {
      setShouldLoad(true);
      return undefined;
    }

    const node = rootRef.current;
    if (!node || !postId) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px 0px", threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [postId, eager]);

  useEffect(() => {
    let cancelled = false;
    if (!postId || !shouldLoad) return undefined;

    setLoaded(false);
    (async () => {
      try {
        const data = await fetchPollByPost(postId);
        if (!cancelled) {
          setPoll(data);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPoll(null);
          setLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [postId, shouldLoad]);

  const pollId = poll?.pollId ?? poll?.PollId;
  const options = poll?.options ?? poll?.Options ?? [];
  const hasPoll = loaded && pollId && options.length > 0;

  if (!shouldLoad) {
    return <div ref={rootRef} className="wlj-poll-card-placeholder" aria-hidden="true" />;
  }

  if (!loaded || !hasPoll) {
    return <div ref={rootRef} className="wlj-poll-card-placeholder" aria-hidden="true" />;
  }

  const expired = poll.isExpired ?? poll.IsExpired;
  const total = poll.totalVotes ?? poll.TotalVotes ?? 0;
  const canVote = !busy && !expired;
  const hasVotes = total > 0;
  const leadingCount = options.reduce((max, opt) => {
    const count = opt.voteCount ?? opt.VoteCount ?? 0;
    return count > max ? count : max;
  }, 0);

  const onVote = async (optionId) => {
    if (!canVote) return;
    setBusy(true);
    try {
      const updated = await votePoll(pollId, [optionId], postId);
      if (updated) setPoll(updated);
    } catch {
      /* toast handled globally if any */
    } finally {
      setBusy(false);
    }
  };

  const onUnvote = async () => {
    if (!canVote) return;
    setBusy(true);
    try {
      const updated = await unvotePoll(pollId, postId);
      if (updated) setPoll(updated);
    } catch {
      /* toast handled globally if any */
    } finally {
      setBusy(false);
    }
  };

  const handleOptionClick = (optionId, mine) => {
    if (!canVote || mine) return;
    onVote(optionId);
  };

  const handleOptionDoubleClick = (optionId, mine) => {
    if (!canVote || !mine) return;
    onUnvote();
  };

  const cardClass = ["wlj-poll-card", nested ? "wlj-poll-card--nested" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={rootRef} className={cardClass}>
      {(poll.description || poll.Description) && (
        <div className="wlj-poll-description">{poll.description || poll.Description}</div>
      )}
      <div className="wlj-poll-options">
        {options.map((opt) => {
          const id = opt.plOptionId ?? opt.PlOptionId;
          const text = opt.text ?? opt.Text;
          const count = opt.voteCount ?? opt.VoteCount ?? 0;
          const mine = opt.votedByMe ?? opt.VotedByMe;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const showResult = hasVotes || expired;
          const isLeading = hasVotes && count > 0 && count === leadingCount;
          const optionClasses = [
            "wlj-poll-option",
            mine ? "is-voted" : "",
            isLeading ? "is-leading" : "",
            canVote ? "is-clickable" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              type="button"
              key={id}
              className={optionClasses}
              disabled={!canVote}
              onClick={() => handleOptionClick(id, mine)}
              onDoubleClick={() => handleOptionDoubleClick(id, mine)}
              aria-pressed={mine}
            >
              {showResult && (
                <span className="wlj-poll-option-fill" style={{ width: `${pct}%` }} />
              )}
              <span className="wlj-poll-option-row">
                <span className="wlj-poll-option-text">
                  {mine && <span className="wlj-poll-check" aria-hidden="true">✓</span>}
                  {text}
                </span>
                {showResult && (
                  <span className="wlj-poll-option-meta">
                    {pct}% · {count}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      <div className="wlj-poll-footer f-12 label">
        {total} {total === 1 ? t("Newsfeed.vote") : t("Newsfeed.votes")}
        {expired ? ` · ${t("Newsfeed.Poll closed")}` : ""}
      </div>
    </div>
  );
};

export default PollCard;
